import { KeyboardArrowLeft } from "@mui/icons-material";
import { Box, Container, Stack, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import Button from "../components/buttons/Button";
import { useApi } from "../contexts/ApiContext";
import { CaseRevisionMeta } from "../pages/cases/types";
import { formatDateWithSlashes, formatTime } from "../utils";
import AppBar from "./AppBar";

interface MainProps {
  children: React.ReactNode;
  hidePages?: boolean;
  id?: string;
}

const StyledMinimalContainer = styled(Box)(({ theme }) => ({
  padding: 0,
  width: "100%",
}));

const RevisionDecoration: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const { id: caseId, revisionId } = useParams();
  const navigate = useNavigate();
  const { client } = useApi();
  const [revisionDate, setRevisionDate] = React.useState<Date | null>(null);

  useEffect(() => {
    if (!caseId || !revisionId) {
      return;
    }

    const fetchRevision = async () => {
      try {
        const response = await client.GET(
          "/v1/cases/{case_id}/revisions/{revision_id}",
          {
            params: {
              path: {
                case_id: caseId,
                revision_id: Number(revisionId),
              },
            },
          }
        );

        if ((response.data as CaseRevisionMeta).created_at) {
          setRevisionDate(
            new Date((response.data as CaseRevisionMeta).created_at)
          );
        }
      } catch (error) {
        console.error("Error fetching revision", error);
      }
    };
    fetchRevision();
  }, [caseId, revisionId]);

  if (!revisionDate) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        p: 2,
        borderColor: "#7C4DFF",
        borderStyle: "solid",
        borderWidth: 8,
        position: "fixed",
        overflow: "auto",
      }}
    >
      <Box zIndex={10} left={0} position={"fixed"} width={"100%"}>
        <Stack
          width={"90%"}
          alignItems={"center"}
          sx={{ backgroundColor: "#7C4DFF", borderRadius: 3 }}
          color={"white"}
          direction={"row"}
          spacing={1}
          alignSelf={"center"}
          margin={"auto"}
          justifyContent={"space-between"}
          px={12}
          left={0}
        >
          <Button
            width={"auto"}
            variant={"text"}
            onClick={() => navigate(`/cases/${caseId}/revisions`)}
          >
            <Stack color={"white"} direction={"row"} spacing={1}>
              <KeyboardArrowLeft />
              <Typography>Back to Revisions</Typography>
            </Stack>
          </Button>

          <Typography color={"white"} fontWeight={"bold"}>
            Revision Mode
          </Typography>

          <Stack color={"white"} direction={"row"} spacing={1}>
            <Typography>Revision Date</Typography>
            <Typography fontWeight={"bold"}>
              {formatDateWithSlashes(revisionDate)}
            </Typography>
            <Typography fontWeight={"bold"}>|</Typography>
            <Typography fontWeight={"bold"}>
              {formatTime(revisionDate, false)}
            </Typography>
          </Stack>
        </Stack>
      </Box>
      <Box pb={8} />
      {children}
    </Box>
  );
};

const Main: React.FC<MainProps> = ({ id, children }) => {
  return (
    <RevisionDecoration>
      <StyledMinimalContainer>
        <AppBar fixedTopBar={false} />
        <Stack
          id={id}
          justifyContent="center"
          alignItems="center"
          padding={4}
          height="100%"
        >
          <Container
            maxWidth="lg"
            sx={{
              maxWidth: "1400px!important",
              minWidth: "1200px!important",
            }}
          >
            {children}
          </Container>
        </Stack>
      </StyledMinimalContainer>
    </RevisionDecoration>
  );
};

export default Main;
